/** P-15 — patient support: ticket list, open a ticket, view + reply to a thread. */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import {
  getMyTicket,
  listMyTickets,
  loadMyAttachment,
  messageFiles,
  openTicket,
  reopenMyTicket,
  replyMyTicket,
} from "../../api/support";
import { AttachmentPicker } from "../../components/ui/AttachmentPicker";
import { AttachmentPreview, type AttachmentTarget } from "../../components/ui/AttachmentPreview";
import { Modal } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";

const CATEGORIES = ["order_issue", "payment_issue", "account_help", "connectivity", "other"];


export function Support() {
  const qc = useQueryClient();
  // Every page, not only the first 25 (SUP-06).
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["my-tickets"],
    queryFn: ({ pageParam }) => listMyTickets(pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });
  const tickets = data?.pages.flatMap((p) => p.data) ?? [];
  const [openForm, setOpenForm] = useState(false);
  // /patient/support/:id (a notification's link) opens that ticket (ACCT-11).
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(id ?? null);
  useEffect(() => setActiveId(id ?? null), [id]);
  const closeTicket = () => {
    setActiveId(null);
    if (id) navigate("/patient/support", { replace: true });
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Support</h2>
        <button onClick={() => setOpenForm(true)} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg">
          New ticket
        </button>
      </div>

      <ul className="space-y-2">
        {tickets.map((t) => (
          <li key={t.id}>
            <button onClick={() => setActiveId(t.id)} className="block w-full rounded-xl border bg-white p-4 text-left shadow-xs hover:border-brand">
              {/* A long subject wraps beside the pill instead of running into it (RESP-14). */}
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 font-medium wrap-anywhere">{t.subject}</span>
                <StatusPill status={t.status} />
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {t.message_count} message{t.message_count === 1 ? "" : "s"} · {new Date(t.last_message_at).toLocaleString()}
              </div>
            </button>
          </li>
        ))}
        {data && tickets.length === 0 && <p className="text-sm text-gray-400">No tickets yet.</p>}
      </ul>
      {hasNextPage && (
        <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="text-sm text-brand hover:underline disabled:opacity-50">
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}

      <Modal open={openForm} title="New support ticket" onClose={() => setOpenForm(false)}>
        <OpenForm onDone={() => { setOpenForm(false); qc.invalidateQueries({ queryKey: ["my-tickets"] }); }} />
      </Modal>

      <Modal open={activeId !== null} title="Ticket" onClose={closeTicket}>
        {activeId && <Thread ticketId={activeId} onChange={() => qc.invalidateQueries({ queryKey: ["my-tickets"] })} />}
      </Modal>
    </div>
  );
}

function OpenForm({ onDone }: { onDone: () => void }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submitKey = useIdempotencyKey();
  const submit = useMutation({
    mutationFn: () => openTicket({
        subject,
        body,
        category,
        files,
        idempotencyKey: submitKey.keyFor({ subject, body, category, files: files.map((f) => [f.name, f.size, f.lastModified]) }),
      }),
    onSuccess: () => {
      submitKey.reset();
      onDone();
    },
    onError: (e) => setError(describeError(e)),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(null); submit.mutate(); }} className="space-y-3">
      <input value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="Subject" maxLength={80} className="w-full rounded-lg border px-3 py-1.5 text-sm" />
      <Select
        value={category}
        options={CATEGORIES.map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1).replace(/_/g, " ") }))}
        onChange={setCategory}
        aria-label="Category"
        className="w-full text-sm"
      />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} required placeholder="Describe the issue" rows={4} className="w-full rounded-lg border px-3 py-1.5 text-sm" />
      <AttachmentPicker files={files} onChange={setFiles} disabled={submit.isPending} />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button disabled={submit.isPending} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
        {submit.isPending ? "Sending…" : "Open ticket"}
      </button>
    </form>
  );
}

function Thread({ ticketId, onChange }: { ticketId: string; onChange: () => void }) {
  const qc = useQueryClient();
  const { data: ticket } = useQuery({ queryKey: ["ticket", ticketId], queryFn: () => getMyTicket(ticketId) });
  const [body, setBody] = useState("");
  // A reply carries files too (SUP-05: it always sent none).
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<AttachmentTarget | null>(null);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["ticket", ticketId] }); onChange(); };

  const replyKey = useIdempotencyKey();
  const reply = useMutation({
    mutationFn: () =>
      replyMyTicket(ticketId, body, files, replyKey.keyFor({ ticketId, body, files: files.map((f) => [f.name, f.size, f.lastModified]) })),
    onSuccess: () => {
      replyKey.reset();
      setBody("");
      setFiles([]);
      invalidate();
    },
    // Not sent (SUP-04: it failed silently): the text stays to try again, the reason shows, and
    // the ticket is fetched again — it may have been resolved meanwhile (TICKET_RESOLVED).
    onError: invalidate,
  });
  const reopen = useMutation({
    mutationFn: () => reopenMyTicket(ticketId),
    onSuccess: () => {
      reply.reset();
      invalidate();
    },
    onError: invalidate,
  });

  if (!ticket) return <p className="text-sm text-gray-400">Loading…</p>;
  const resolved = ticket.status === "resolved";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 font-medium wrap-anywhere">{ticket.subject}</span>
        <StatusPill status={ticket.status} />
      </div>
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {ticket.messages.map((m) => (
          <div key={m.id} className={`rounded-lg p-3 text-sm ${m.author_role === ticket.opener_role ? "bg-gray-50" : "bg-brand/10"}`}>
            <div className="text-xs text-gray-400">{m.author_role} · {new Date(m.created_at).toLocaleString()}</div>
            {/* The author's line breaks stay, and a long URL or reference wraps inside the bubble (RESP-13). */}
            <div className="whitespace-pre-wrap wrap-anywhere">{m.body}</div>
            {messageFiles(m).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {messageFiles(m).map((file) => (
                  <button
                    key={file.index}
                    type="button"
                    onClick={() => setPreview({ filename: file.filename, load: () => loadMyAttachment(ticket.id, m.id, file.index) })}
                    className="max-w-[12rem] truncate rounded-md border bg-white px-2 py-0.5 text-xs text-brand hover:border-brand"
                    title={file.filename}
                  >
                    📎 {file.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {resolved ? (
        <button onClick={() => reopen.mutate()} disabled={reopen.isPending} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50">
          {reopen.isPending ? "Reopening…" : "Reopen ticket"}
        </button>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) reply.mutate(); }} className="space-y-2">
          <div className="flex gap-2">
            <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" className="min-w-0 flex-1 rounded-lg border px-3 py-1.5 text-sm" />
            <button disabled={reply.isPending || !body.trim()} className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50">
              {reply.isPending ? "Sending…" : "Send"}
            </button>
          </div>
          <AttachmentPicker files={files} onChange={setFiles} disabled={reply.isPending} />
        </form>
      )}
      {reply.isError && <p className="text-sm text-red-600">Not sent: {describeError(reply.error)}</p>}
      {reopen.isError && <p className="text-sm text-red-600">{describeError(reopen.error)}</p>}
      <AttachmentPreview target={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === "resolved" ? "bg-emerald-50 text-emerald-700" : status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600";
  // One piece: "in progress" never breaks onto two lines (RESP-14).
  return <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${tone}`}>{status.replace("_", " ")}</span>;
}
